import React, { Component } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import paper from 'paper';

import {
  Annotation,
  AnnotationType,
  BoundingBox,
  ImageAnnotation,
  PathAnnotation,
  TextAnnotation,
} from '@paragon/contract-library';
import { getLoggedInUser } from '@paragon/login-tools';
import { enqueueWarning } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';

import { editAnnotation, EditProps, placeTextOnActivePage } from '../../../actions/annotations';
import { RootState } from '../../../store';
import PathOptions from './PathOptions';
import RotationOptions from './RotationOptions';
import SharingOptions, { isAnnotationShareable } from './SharingOptions';
import SizeOptions from './SizeOptions';
import TextOptions from './TextOptions';
import VariableOptions from './VariableOptions';

interface OwnProps {
  readonly startingText?: string;
  readonly edit?: 'edit';
  readonly annotationBeingEdited?: Annotation;
  readonly textContent?: string | null;
  readonly fontSize?: number | undefined;
  readonly color?: string;
  readonly annotationType?: AnnotationType;
  readonly annotationBoundingBox?: BoundingBox;
  readonly rotation?: number;
  readonly itemBeingEdited?: paper.Item;
}
type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = OwnProps & StateProps & DispatchProps;

export class UnconnectedEditAnnotation extends Component<Props> {
  path?: PathOptions | null;
  text?: TextOptions | null;
  rotation?: RotationOptions | null;
  size?: SizeOptions | null;
  sharedWith?: SharingOptions | null;
  variableOptions?: VariableOptions | null;

  onSubmitAnnotationClick = () => {
    if (paper.view == null || (this.isTextAnnotation && this.text!.prohibitEditing)) {
      return;
    }

    if (this.props.edit) {
      let updatedProperties: EditProps = {} as EditProps;

      for (const section of [this.path, this.text, this.rotation, this.size, this.sharedWith, this.variableOptions]) {
        if (section != null) {
          Object.assign(updatedProperties, section.updatedProperties);
        }
      }

      if (this.props.annotationType === 'image') {
        updatedProperties = {
          ...updatedProperties,
          source: (this.props.annotationBeingEdited as ImageAnnotation).source,
        };
      }

      this.props.editAnnotation(this.props.annotationBeingEdited!.guid, updatedProperties);
      this.props.closeCurrentPopup();
    } else {
      const { sharedWith } = this.sharedWith!.updatedProperties;
      const { contents, color, fontSize, fontFamily, isItalic, isBold, isUnderlined } = this.text!.updatedProperties;
      const { rotationAngle } = this.rotation!.updatedProperties;
      const translationArea = this.variableOptions ? this.variableOptions.updatedProperties.translationArea : null;

      this.props.placeTextOnActivePage(
        contents,
        color,
        fontSize,
        fontFamily,
        sharedWith,
        rotationAngle,
        null,
        translationArea,
        { isItalic, isBold, isUnderlined },
      );
      this.props.closeCurrentPopup();
    }
  };

  get isPlacingText() {
    return this.props.edit !== 'edit';
  }

  get isTextAnnotation() {
    const { annotationBeingEdited } = this.props;

    return this.isPlacingText || (!!annotationBeingEdited && annotationBeingEdited.type === 'text');
  }

  get isEditingVariable() {
    return (
      this.isPlacingText ||
      (this.props.annotationBeingEdited?.type === 'text' && this.props.annotationBeingEdited.contents.startsWith('$'))
    );
  }

  get shouldShowVariableOptions() {
    const { currentUser } = this.props;

    return (
      this.isEditingVariable &&
      (currentUser!.permissions.includes('root') || currentUser!.organization === 'Paragon Component Systems')
    );
  }

  render() {
    const { annotationType } = this.props;

    const submitText = this.isPlacingText ? 'Place Text' : 'Edit Annotation';

    let startingText = '';
    if (this.props.textContent != null) {
      startingText = this.props.textContent;
    } else if (this.props.startingText != null) {
      startingText = this.props.startingText;
    }

    const textSection =
      this.isPlacingText || annotationType === 'text' ? (
        <TextOptions
          defaultValue={startingText}
          annotationBeingEdited={this.props.annotationBeingEdited as TextAnnotation | undefined}
          onSizeChange={(scalingPercentage) => {
            if (this.size) this.size.adjustDimensions(scalingPercentage);
          }}
          ref={(section: TextOptions | null) => {
            this.text = section;
          }}
        />
      ) : null;

    const sizeSection =
      !this.isPlacingText && annotationType !== 'path' ? (
        <SizeOptions
          itemBeingEdited={this.props.itemBeingEdited!}
          annotationBeingEdited={this.props.annotationBeingEdited}
          resizingDisabled={this.isPlacingText}
          individualScalingDisabled={this.isTextAnnotation}
          onAdjustFontSize={(scalingPercentage) => {
            if (this.text) this.text.adjustFontSize(scalingPercentage);
          }}
          ref={(section) => {
            this.size = section;
          }}
        />
      ) : null;

    const rotationSection =
      annotationType !== 'path' ? (
        <RotationOptions
          annotationBeingEdited={this.props.annotationBeingEdited}
          ref={(section) => {
            this.rotation = section;
          }}
        />
      ) : null;

    const pathAnnotationSection =
      annotationType === 'path' ? (
        <PathOptions
          annotationBeingEdited={this.props.annotationBeingEdited as PathAnnotation}
          ref={(section) => {
            this.path = section;
          }}
        />
      ) : null;

    const annotationSharingControl =
      this.isPlacingText || isAnnotationShareable(this.props.annotationBeingEdited!) ? (
        <SharingOptions
          annotationBeingEdited={this.props.annotationBeingEdited}
          enqueueWarning={this.props.enqueueWarning}
          isPlacingText={this.isPlacingText}
          currentUserEmail={this.props.currentUser!.email}
          ref={(section) => {
            this.sharedWith = section;
          }}
        />
      ) : null;

    const variableOptionsSection = this.shouldShowVariableOptions ? (
      <VariableOptions
        annotationBeingEdited={this.props.annotationBeingEdited as TextAnnotation | undefined}
        ref={(section) => {
          this.variableOptions = section;
        }}
      />
    ) : null;

    return (
      <Dialog title="Edit Annotation" onSubmit={this.onSubmitAnnotationClick} submitText={submitText}>
        {textSection}

        {sizeSection}

        {rotationSection}
        {pathAnnotationSection}
        {annotationSharingControl}

        {variableOptionsSection}
      </Dialog>
    );
  }
}

function mapStateToProps(state: RootState) {
  return {
    currentUser: getLoggedInUser(state),
  };
}

const actionCreators = {
  placeTextOnActivePage,
  editAnnotation,
  closeCurrentPopup,
  enqueueWarning,
};

export default connect(mapStateToProps, actionCreators)(UnconnectedEditAnnotation);
